import { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { SelectOptionType } from '../../atoms/Select';
import {
    propertyTypesApi,
    propertyTypeApi,
    popularPlaceApi,
    districtApi,
    communeApi
} from '../../../core/http/httpClient';

// Cache keys for localStorage
const CACHE_KEYS = {
    PROPERTY_TYPES: 'searchForm_propertyTypes_v2',
    POPULAR_PLACES: 'searchForm_popularPlaces',
    DISTRICTS: 'searchForm_districts_v2',
    CACHE_TIMESTAMP: 'searchForm_timestamp'
};

// Cache duration: 1 hour
const CACHE_DURATION = 60 * 60 * 1000;

// Helper functions for localStorage caching
const getCachedData = (key: string) => {
    try {
        const cached = localStorage.getItem(key);
        return cached ? JSON.parse(cached) : null;
    } catch (error) {
        return null;
    }
};

const setCachedData = (key: string, data: any) => {
    try {
        localStorage.setItem(key, JSON.stringify(data));
        localStorage.setItem(CACHE_KEYS.CACHE_TIMESTAMP, Date.now().toString());
    } catch (error) {
    }
};

const isCacheValid = () => {
    try {
        const timestamp = localStorage.getItem(CACHE_KEYS.CACHE_TIMESTAMP);
        if (!timestamp) return false;
        return (Date.now() - parseInt(timestamp)) < CACHE_DURATION;
    } catch (error) {
        return false;
    }
};

const clearCache = () => {
    try {
        Object.values(CACHE_KEYS).forEach(key => {
            localStorage.removeItem(key);
        });
        // Also clear any property type caches
        ['1', '2', 'all'].forEach(category => {
            localStorage.removeItem(`${CACHE_KEYS.PROPERTY_TYPES}_${category}`);
        });
    } catch (error) {
    }
};

interface UseSearchFormDataProps {
    categoryType?: string; // For filtering property types by category
}



interface TranslatedOption extends SelectOptionType {
    name_kh?: string;
    name_zh?: string;
}

export const useSearchFormData = ({ categoryType }: UseSearchFormDataProps = {}) => {
    const { currentLanguage } = useLanguage();
    const [propertyTypes, setPropertyTypes] = useState<TranslatedOption[]>([]);
    const [popularPlaces, setPopularPlaces] = useState<TranslatedOption[]>([]);
    const [districts, setDistricts] = useState<TranslatedOption[]>([]);
    const [communes, setCommunes] = useState<TranslatedOption[]>([]);
    const [loading, setLoading] = useState(true);

    // Helper to get translated name
    const getTranslatedName = useCallback((item: TranslatedOption) => {
        const lang = currentLanguage?.toLowerCase();
        let translated = item.name;
        if (lang?.startsWith('km') || lang?.startsWith('kh')) {
            translated = item.name_kh || item.name;
        } else if (lang?.startsWith('zh')) {
            translated = item.name_zh || item.name;
        }

        return translated;
    }, [currentLanguage]);

    // Map options with translation
    const mapOptions = useCallback((options: TranslatedOption[]) => {
        return options.map(opt => ({
            ...opt,
            name: getTranslatedName(opt)
        }));
    }, [getTranslatedName]);

    // This is intentionally commented out as we handle property types inline
    // const fetchPropertyTypes = useCallback(async () => { ... }, [categoryType]);

    // Fetch popular places
    const fetchPopularPlaces = useCallback(async () => {
        try {
            // Check localStorage cache first
            if (isCacheValid()) {
                const cachedData = getCachedData(CACHE_KEYS.POPULAR_PLACES);
                if (cachedData) {
                    setPopularPlaces(cachedData);
                    return;
                }
            }

            const response = await popularPlaceApi.call({});
            const formattedData = response.data.map((item: any) => ({
                id: parseInt(item.id),
                name: item.name,
                name_kh: item.name_kh,
                name_zh: item.name_zh,
                value: item.id
            }));

            // Cache the data and update state
            setCachedData(CACHE_KEYS.POPULAR_PLACES, formattedData);
            setPopularPlaces(formattedData);
        } catch (error) {
            console.error('Error fetching popular places:', error);
        }
    }, []);

    // Fetch districts for city 1 (Phnom Penh)
    const fetchDistricts = useCallback(async () => {
        try {
            // Check localStorage cache first
            if (isCacheValid()) {
                const cachedData = getCachedData(CACHE_KEYS.DISTRICTS);
                if (cachedData) {
                    setDistricts(cachedData);
                    return;
                }
            }

            const response = await districtApi.call({ city_id: '1' });
            const formattedData = response.data.map((item: any) => ({
                id: parseInt(item.id),
                name: item.name,
                name_kh: item.name_kh,
                name_zh: item.name_zh,
                value: item.id
            }));

            // Cache the data and update state
            setCachedData(CACHE_KEYS.DISTRICTS, formattedData);
            setDistricts(formattedData);
        } catch (error) {
            console.error('Error fetching districts:', error);
        }
    }, []);

    // Fetch communes for a specific district
    const fetchCommunes = useCallback(async (districtId: string) => {
        try {
            const response = await communeApi.call({ district_id: districtId });
            const formattedData = response.data.map((item: any) => ({
                id: parseInt(item.id),
                name: item.name,
                name_kh: item.name_kh,
                name_zh: item.name_zh,
                value: item.id
            }));
            setCommunes(formattedData);
        } catch (error) {
            console.error('Error fetching communes:', error);
            setCommunes([]);
        }
    }, []);

    // Load cached data immediately on mount
    useEffect(() => {
        const loadInitialData = () => {
            setLoading(true);

            // Load cached data immediately if available
            if (isCacheValid()) {
                const cachedPropertyTypes = getCachedData(`${CACHE_KEYS.PROPERTY_TYPES}_${categoryType || 'all'}`);
                const cachedPopularPlaces = getCachedData(CACHE_KEYS.POPULAR_PLACES);
                const cachedDistricts = getCachedData(CACHE_KEYS.DISTRICTS);

                if (cachedPropertyTypes) setPropertyTypes(cachedPropertyTypes);
                if (cachedPopularPlaces) setPopularPlaces(cachedPopularPlaces);
                if (cachedDistricts) setDistricts(cachedDistricts);

                // If all data is cached, we're done loading
                if (cachedPropertyTypes && cachedPopularPlaces && cachedDistricts) {
                    setLoading(false);
                    return;
                }
            }

            // Fetch missing data
            const fetchMissingData = async () => {
                const promises = [];

                // Only fetch if not cached or cache is invalid
                if (!getCachedData(CACHE_KEYS.POPULAR_PLACES) || !isCacheValid()) {
                    promises.push(fetchPopularPlaces());
                }
                if (!getCachedData(CACHE_KEYS.DISTRICTS) || !isCacheValid()) {
                    promises.push(fetchDistricts());
                }

                // Property types will be handled by the categoryType effect

                if (promises.length > 0) {
                    await Promise.all(promises);
                }
                setLoading(false);
            };

            fetchMissingData();
        };

        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array to run only once on mount - functions are handled inline

    // Handle category type changes
    useEffect(() => {
        const handleCategoryChange = async () => {
            const cacheKey = `${CACHE_KEYS.PROPERTY_TYPES}_${categoryType || 'all'}`;

            // Check if we have cached data for this category
            if (isCacheValid()) {
                const cachedData = getCachedData(cacheKey);
                if (cachedData) {
                    setPropertyTypes(cachedData);
                    return;
                }
            }

            // Fetch new data if not cached
            try {
                let response;
                if (categoryType) {
                    response = await propertyTypeApi.call({ 'str:category_type': categoryType });
                } else {
                    response = await propertyTypesApi.call({});
                }

                const formattedData = response.data.map((item: any) => ({
                    id: item.id,
                    name: item.name,
                    name_kh: item.name_kh,
                    name_zh: item.name_zh,
                    value: item.id
                }));

                setCachedData(cacheKey, formattedData);
                setPropertyTypes(formattedData);
            } catch (error) {
                console.error('Error fetching property types for category:', error);
            }
        };

        handleCategoryChange();
    }, [categoryType]); // Only depend on categoryType

    return {
        propertyTypes: mapOptions(propertyTypes),
        popularPlaces: mapOptions(popularPlaces),
        districts: mapOptions(districts),
        communes: mapOptions(communes),
        loading,
        fetchCommunes
    };
};

// Export cache management for debugging
export { clearCache }; 